import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { pageAnimation } from '@app/core/animations/element.animations';
import { OrganisationConfig } from '@app/model/organisationConfig';
import { AuthService } from '@app/services/auth.service';
import { ContactService } from '@app/services/contact.service';
import { AppState, getLoggedIn, getLoggedInContact } from '@app/store/app.reducers';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, of } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { Module } from './home.component.models';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    animations: [pageAnimation]
})
export class HomeComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private defaultSubheading = 'What would you like to do today?';

    @HostBinding('@pageAnimation') animate = true;
    modules$: Observable<Module[]>;
    assetsDir = 'assets/home';
    subheading = this.defaultSubheading;

    constructor(private store: Store<AppState>, private contactService: ContactService) { }

    ngOnInit() {
        this.modules$ = this.getModules$();

        this.store
            .pipe(
                select(getLoggedInContact),
                takeUntil(this.destroy$)
            )
            .subscribe(contact => {
                if (contact?.firstName) {
                    this.subheading = `Hello ${contact.firstName}, what would you like to do today?`;
                } else {
                    this.subheading = this.defaultSubheading;
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getModules$(): Observable<Module[]> {
        return this.store.pipe(
            select(getLoggedIn),
            mergeMap(isLoggedin => this.getOrganisationConfig$(isLoggedin)),
            map(this.getModules)
        );
    }

    private getOrganisationConfig$(loggedin: boolean): Observable<OrganisationConfig> {
        if (!loggedin) {
            return of(new OrganisationConfig());
        } else {
            return this.contactService.getOrganisationConfig(AuthService.getAccessToken());
        }
    }

    private getModules(organisationConfig: OrganisationConfig) {
        const modules: Module[] = [];

        const labTestsModule: Module = {
            titleLine1: 'Order',
            titleLine2: 'Laboratory',
            titleLine3: 'Tests',
            description: 'Order your tests ahead of time to speed up processing in the lab.' +
        ' View test prices and our test catalogue range.',
            icon: 'lab-tests-v2.png',
            url: 'lab-orders/create',
            id: 'home-lab-test-module'
        };

        const samplingSuppliesModule: Module = {
            titleLine1: 'Order',
            titleLine2: 'Sampling',
            titleLine3: 'Supplies',
            description: 'Order test kits or replenish your stock of sampling supplies.' +
        ' You can also purchase sampling tools such as augers and pasture shears.',
            icon: 'supplies-v2.png',
            url: 'sampling-supplies',
            id: 'home-sampling-supplies-module'
        };

        const myJobsModule: Module = {
            titleLine1: 'Track',
            titleLine2: 'My',
            titleLine3: 'Jobs',
            description: 'Track the progress of your testing, view reports, invoices, and job details,' +
        ' and tracking links for sampling supplies as they are couriered to you.',
            icon: 'my-jobs-v2.png',
            url: 'orders/history',
            id: 'home-my-jobs-module',
            fullDocumentNavigation: true
        };

        if (organisationConfig.canOrderLabTests) {
            modules.push(labTestsModule);
        }

        if (organisationConfig.canOrderSamplingSupplies) {
            modules.push(samplingSuppliesModule);
        }

        modules.push(myJobsModule);

        return modules;
    }
}
